import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";
import InputField from "../../../components/inputs/inputField";
import SelectField from "../../../components/inputs/selectField";
import ViewsTable from "../../../components/tables/ViewsTable";
import LoadingWizard from "../../../components/spinner/wizard";
import XLSX from "xlsx";
import { saveAs } from "file-saver";

class Payments extends Component {
    constructor(props) {
        super(props);

        // Process initial column data
        const { columns, columnTypes, toggledColumns, normalizedColumns } = this.processBaseColumns();

        this.state = {
            loading: false,
            columns,
            columnTypes,
            normalizedColumns,
            toggledColumns,
            selectedColumnSort: { key: "purchasedAt", type: "timestamp" },
            sortDesc: true,
            searchTerm: "",
            searching: false,
            dateFilter: "all",
            checkedPayment: {},
            processedPayments: null,
            filteredPayments: null,
            checked: {}, // Add this to fix the error
            activeRow: null // Add this to fix the error
        };

        this.searchTimeout = null;
    }

    processBaseColumns = () => {
        const columns = {
            stripeCreated: "Date",
            stripeConfirmationId: "Transaction ID",
            attendeeName: "Attendee",
            attendeeEmail: "Email",
            ticketLabel: "Ticket",
            stripeAmount: "Amount",
            stripeStatusDisplay: "Payment Status"
        };

        const columnTypes = {
            stripeCreated: "timestamp",
            stripeConfirmationId: "text",
            attendeeName: "text",
            attendeeEmail: "text",
            ticketLabel: "text",
            stripeAmount: "currency",
            stripeStatusDisplay: "text"
        };

        const toggledColumns = {};
        const normalizedColumns = Object.keys(columns).map((cfc) => {
            toggledColumns[cfc] = true;
            return {
                label: columns[cfc],
                key: cfc,
                value: cfc,
                type: columnTypes[cfc],
                sortAsc: false,
                sortDesc: cfc === "purchasedAt" ? true : false,
            };
        });

        return { columns, columnTypes, toggledColumns, normalizedColumns };
    };

    componentDidMount() {
        this.processPaymentsData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.payments !== this.props.payments) {
            this.processPaymentsData();
        }
    }

    processPaymentsData = () => {
        const { payments } = this.props;

        // Make sure each payment has an _id for the ViewsTable component
        const processedPayments = payments.map(payment => ({
            ...payment,
            _id: payment.stripeConfirmationId, // Use stripeConfirmationId as a unique identifier
            formattedDate: moment(payment.purchasedAt).format("MMM DD, YYYY h:mm a"),
            formattedAmount: `$${payment.totalAmount.toFixed(2)}`,
            statusClass: payment.status === "registered" ? "green" :
                payment.status === "refunded" ? "orange" : "blue",
            statusLabel: payment.status === "registered" ? "Paid" :
                payment.status === "refunded" ? "Refunded" : payment.status
        }));

        this.setState({
            processedPayments,
            filteredPayments: processedPayments
        }, () => {
            this.applySorting();
        });
    };

    applySorting = () => {
        const { selectedColumnSort, sortDesc, processedPayments } = this.state;

        if (!selectedColumnSort || !processedPayments) return;

        const sortedData = [...processedPayments].sort((a, b) => {
            const columnType = selectedColumnSort.type;

            if (a[selectedColumnSort.key] && b[selectedColumnSort.key]) {
                switch (columnType) {
                    case "timestamp":
                        return new Date(a[selectedColumnSort.key]) - new Date(b[selectedColumnSort.key]);
                    case "currency":
                        return parseFloat(a[selectedColumnSort.key]) - parseFloat(b[selectedColumnSort.key]);
                    case "text":
                    default:
                        return String(a[selectedColumnSort.key]).localeCompare(String(b[selectedColumnSort.key]));
                }
            } else if (a[selectedColumnSort.key]) {
                return 1;
            }
            return -1;
        });

        const finalSorted = sortDesc ? sortedData.reverse() : sortedData;
        this.applyFilters(finalSorted);
    };

    applyFilters = (data = null) => {
        const { searchTerm, dateFilter, processedPayments } = this.state;
        const dataToFilter = data || processedPayments;

        if (!dataToFilter) return;

        // Filter by search term
        let filtered = [...dataToFilter];
        if (searchTerm.length > 0) {
            const searchTermLower = searchTerm.toLowerCase();
            filtered = filtered.filter(payment =>
                payment.attendeeName.toLowerCase().includes(searchTermLower) ||
                payment.attendeeEmail.toLowerCase().includes(searchTermLower) ||
                payment.stripeConfirmationId.toLowerCase().includes(searchTermLower) ||
                payment.ticketLabel.toLowerCase().includes(searchTermLower)
            );
        }

        // Filter by date
        if (dateFilter !== "all") {
            const now = new Date();
            filtered = filtered.filter(payment => {
                const paymentDate = new Date(payment.purchasedAt);

                switch (dateFilter) {
                    case "today":
                        return paymentDate.toDateString() === now.toDateString();
                    case "yesterday":
                        const yesterday = new Date(now);
                        yesterday.setDate(yesterday.getDate() - 1);
                        return paymentDate.toDateString() === yesterday.toDateString();
                    case "week":
                        const weekAgo = new Date(now);
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return paymentDate >= weekAgo;
                    case "month":
                        const monthAgo = new Date(now);
                        monthAgo.setDate(monthAgo.getDate() - 30);
                        return paymentDate >= monthAgo;
                    default:
                        return true;
                }
            });
        }

        this.setState({ filteredPayments: filtered });
    };

    s2ab = (s) => {
        var buf = new ArrayBuffer(s.length);
        var view = new Uint8Array(buf);
        for (var i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
    };

    downloadExcel = () => {
        const { filteredPayments } = this.state;
        const { name } = this.props;

        const headers = ["Date", "Transaction ID", "Attendee", "Email", "Ticket", "Amount", "Payment Status"];
        
        const rows = filteredPayments.map(payment => [
            moment(payment.stripeCreated).format("MM/DD/YYYY h:mm a"),
            payment.stripeConfirmationId,
            payment.attendeeName,
            payment.attendeeEmail,
            payment.ticketLabel,
            `$${payment.stripeAmount}`,
            payment.stripeStatusDisplay
        ]);


        var wb = XLSX.utils.book_new();
        wb.Props = {
            Title: `${name || "Payments"} Report`,
            Subject: "Payment Transactions",
            Author: "Simple Events",
            CreatedDate: new Date(),
        };
        wb.SheetNames.push("Payments");

        var ws_data = [headers, ...rows];
        var ws = XLSX.utils.aoa_to_sheet(ws_data);
        wb.Sheets["Payments"] = ws;

        var wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });

        saveAs(
            new Blob([this.s2ab(wbout)], { type: "application/octet-stream" }),
            `${name || "Payments"}_Report_${moment().format("YYYY-MM-DD")}.xlsx`
        );
    };

    sortData = (column, sortAsc, sortDesc) => {
        const { normalizedColumns } = this.state;
        const updatedColumns = normalizedColumns.map((col) => {
            if (col.key === column.key) {
                col.sortAsc = sortAsc;
                col.sortDesc = sortDesc;
            } else {
                col.sortAsc = false;
                col.sortDesc = false;
            }
            return col;
        });

        this.setState({
            normalizedColumns: updatedColumns,
            selectedColumnSort: column,
            sortDesc: sortDesc ? true : false,
        }, () => this.applySorting());
    };

    handleSearchChange = (e) => {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.setState({ searchTerm: e.target.value }, () => {
            this.searchTimeout = setTimeout(() => {
                this.setState({ searching: this.state.searchTerm !== "" }, () => {
                    this.applyFilters();
                });
            }, 300);
        });
    };

    clearSearch = () => {
        this.setState({ searchTerm: "", searching: false }, () => this.applyFilters());
    };

    handleDateFilterChange = (option) => {
        this.setState({ dateFilter: option.value }, () => this.applyFilters());
    };

    // Add this method required by ViewsTable
    checkRow = (id) => {
        this.setState(prevState => ({
            checked: {
                ...prevState.checked,
                [id]: !prevState.checked[id]
            }
        }));
    };

    // Add this method required by ViewsTable
    checkAllRows = (ids) => {
        const checked = {};
        ids.forEach(id => {
            checked[id] = true;
        });
        this.setState({ checked });
    };

    renderSearchInput = () => {
        const { searchTerm } = this.state;
        return (
            <InputField
                classes="m-0"
                placeholder="Search transactions..."
                clickIcon={true}
                required={false}
                value={searchTerm}
                onChange={this.handleSearchChange}
                prefix={
                    <i
                        className="las la-search"
                        style={{ marginRight: 5, transform: "rotate(270deg)" }}
                    />
                }
                inputIcon={
                    searchTerm !== "" && (
                        <i
                            onMouseDown={this.clearSearch}
                            className="las la-times-circle"
                            style={{
                                visibility: searchTerm ? "visible" : "hidden",
                            }}
                        />
                    )
                }
            />
        );
    };

    calculateSummaryStats = () => {
        const { processedPayments, filteredPayments, dateFilter } = this.state;

        if (!processedPayments) return { totalTransactions: 0, totalRevenue: 0, periodRevenue: 0 };

        const totalTransactions = filteredPayments.length;

        const totalRevenue = filteredPayments.reduce((sum, payment) =>
            sum + payment.totalAmount, 0);

        // Calculate revenue for last 30 days
        const now = new Date();
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const periodRevenue = processedPayments
            .filter(payment => new Date(payment.purchasedAt) >= thirtyDaysAgo)
            .reduce((sum, payment) => sum + payment.totalAmount, 0);

        return { totalTransactions, totalRevenue, periodRevenue };
    };

    render() {
        const { loading, normalizedColumns, toggledColumns, filteredPayments, dateFilter, checked, activeRow } = this.state;

        if (loading) {
            return <LoadingWizard text="LOADING PAYMENTS DATA" />;
        }

        if (!filteredPayments) {
            return <LoadingWizard text="PROCESSING DATA" />;
        }

        const { totalTransactions, totalRevenue, periodRevenue } = this.calculateSummaryStats();

        // Customize the cell renderer for the ViewsTable
        const cellRenderer = (row, column) => {
            if (column.key === 'status') {
                return (
                    <span className={`badge ${row.status === "registered" ? "green" :
                        row.status === "refunded" ? "orange" : "blue"}`}>
                        {row.status === "registered" ? "Paid" :
                            row.status === "refunded" ? "Refunded" : row.status}
                    </span>
                );
            }

            if (column.key === 'totalAmount') {
                return `$${row.totalAmount.toFixed(2)}`;
            }

            if (column.key === 'purchasedAt') {
                return moment(row.purchasedAt).format("MMM DD, YYYY h:mm a");
            }

            if (column.key === 'stripeConfirmationId') {
                return <span className="monospace">{row.stripeConfirmationId}</span>;
            }

            return row[column.key];
        };

        const emptyMessage = "No payment transactions found";

        return (
            <div className="sectionContainer">
                <div style={{ height: 'calc(100% - 150px)' }}>
                    {/* Summary Stats Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '20px',
                        marginTop: '10px'
                    }}>
                        <div className="statCard" style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            textAlign: 'center',
                            width: '32%',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                            <div className="statValue" style={{
                                fontSize: '28px',
                                fontWeight: 'bold',
                                color: '#444'
                            }}>
                                {totalTransactions}
                            </div>
                            <div className="statLabel" style={{
                                fontSize: '14px',
                                color: '#666'
                            }}>
                                Total Transactions
                            </div>
                        </div>
                        <div className="statCard" style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            textAlign: 'center',
                            width: '32%',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                            <div className="statValue" style={{
                                fontSize: '28px',
                                fontWeight: 'bold',
                                color: '#444'
                            }}>
                                ${totalRevenue.toFixed(2)}
                            </div>
                            <div className="statLabel" style={{
                                fontSize: '14px',
                                color: '#666'
                            }}>
                                Total Revenue
                            </div>
                        </div>
                        <div className="statCard" style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            textAlign: 'center',
                            width: '32%',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                            <div className="statValue" style={{
                                fontSize: '28px',
                                fontWeight: 'bold',
                                color: '#444'
                            }}>
                                ${periodRevenue.toFixed(2)}
                            </div>
                            <div className="statLabel" style={{
                                fontSize: '14px',
                                color: '#666'
                            }}>
                                Last 30 Days Revenue
                            </div>
                        </div>
                    </div>

                    <div className="actionButtonContainer" style={{ marginBottom: 15 }}>
                        <div className="flex aic gap10">
                            {this.renderSearchInput()}
                            <SelectField
                                classes="w-150 m-0"
                                placeholder="Filter by date"
                                value={{
                                    value: dateFilter,
                                    label: dateFilter === "all" ? "All time" :
                                        dateFilter === "today" ? "Today" :
                                            dateFilter === "yesterday" ? "Yesterday" :
                                                dateFilter === "week" ? "Last 7 days" :
                                                    dateFilter === "month" ? "Last 30 days" : "All time"
                                }}
                                onChange={this.handleDateFilterChange}
                                options={[
                                    { value: "all", label: "All time" },
                                    { value: "today", label: "Today" },
                                    { value: "yesterday", label: "Yesterday" },
                                    { value: "week", label: "Last 7 days" },
                                    { value: "month", label: "Last 30 days" }
                                ]}
                            />
                        </div>
                        <div className="sectionOptionsContainer">
                            <button
                                onClick={this.downloadExcel}
                                className="actionButton p-7"
                            >
                                <div>
                                    <i className="las la-download" />
                                </div>
                            </button>
                        </div>
                    </div>

                    <ViewsTable
                        title={"Payment Transactions"}
                        style={{ margin: 0 }}
                        noDescription={true}
                        columns={normalizedColumns}
                        toggledColumns={toggledColumns}
                        data={filteredPayments || []}
                        mainColumnActive={false}
                        disableCheck={true}
                        sortData={this.sortData}
                        cellRenderer={cellRenderer}
                        emptyMessage={emptyMessage}
                        checked={checked}
                        checkRow={this.checkRow}
                        checkAllRows={this.checkAllRows}
                        activeRow={activeRow}
                        disableColumnResizing={false}
                        readOnly={true}
                        classes=""
                    />
                </div>
            </div>
        );
    }
}

export default withRouter(Payments);